import { useEffect, useState } from "react";

import { getAccessToken } from "../auth/tokenStore";

// jmix-8.2/8.4: authenticated image streaming. The SPA access token lives in memory and rides the
// Authorization header — it CANNOT ride an <img src> (the browser issues that request without our
// header, so the portal's bearer-gated image endpoints would 401). So we fetch the bytes with the
// bearer, wrap them in an object-URL, and hand THAT to the <img>/<ImagePreview>. The object-URL is
// revoked on unmount / when the path changes, so blobs don't leak (backoffice §9 + migration guide §8).

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Fetch an authenticated image and return an object-URL, or null if absent (404) / unauthenticated. */
export async function fetchAuthImageObjectUrl(path: string): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }
  try {
    const response = await fetch(`${API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return null; // 404 (no photo / not linked) degrades to the placeholder, never an error
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Hook form: resolves `path` (an API path like `/portal/organizations/{uuid}/logo`) to an object-URL,
 * revoking the previous one on change/unmount. `path == null` yields no fetch (the caller knows there
 * is no image — e.g. `hasPhoto === false`). Returns `undefined` while loading, `null` when absent.
 */
export function useAuthImage(path: string | null): string | null | undefined {
  const [url, setUrl] = useState<string | null | undefined>(path == null ? null : undefined);

  useEffect(() => {
    if (path == null) {
      setUrl(null);
      return;
    }
    let active = true;
    let objectUrl: string | null = null;
    setUrl(undefined);
    void (async () => {
      const resolved = await fetchAuthImageObjectUrl(path);
      if (active) {
        objectUrl = resolved;
        setUrl(resolved);
      } else if (resolved) {
        URL.revokeObjectURL(resolved); // unmounted mid-flight — don't leak
      }
    })();
    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [path]);

  return url;
}
